import { useRef, useState } from "react";
import Compressor from "compressorjs";
import { Upload, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";
import { ImageCropDialog } from "@/components/ImageCropDialog";

async function detectImageMime(file: File): Promise<string | null> {
  const buf = await file.slice(0, 12).arrayBuffer();
  const b = new Uint8Array(buf);
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "image/png";
  if (
    b[0] === 0x52 &&
    b[1] === 0x49 &&
    b[2] === 0x46 &&
    b[3] === 0x46 &&
    b[8] === 0x57 &&
    b[9] === 0x45 &&
    b[10] === 0x42 &&
    b[11] === 0x50
  )
    return "image/webp";
  // AVIF: ftyp box at bytes 4-7, major brand "avif" or "avis" at bytes 8-11
  if (
    b[4] === 0x66 &&
    b[5] === 0x74 &&
    b[6] === 0x79 &&
    b[7] === 0x70 &&
    ((b[8] === 0x61 && b[9] === 0x76 && b[10] === 0x69 && b[11] === 0x66) ||
      (b[8] === 0x61 && b[9] === 0x76 && b[10] === 0x69 && b[11] === 0x73))
  )
    return "image/avif";
  return null;
}

type ImageUploadProps = {
  label: string;
  labelExtra?: React.ReactNode;
  aspectRatio: "1:1" | "16:9" | "3:1";
  onFileSelect: (file: File) => void;
  onClear: () => void;
  currentUrl?: string;
  initialFile?: File;
  isUploading?: boolean;
  disabled?: boolean;
  // Hides Change/Remove entirely instead of just disabling them — for a form that's locked read-only.
  readOnly?: boolean;
  id?: string;
  error?: string;
  className?: string;
  enableCrop?: boolean;
  // False when there's no backend path to actually clear this field (e.g. store logo/cover) — only lets you undo a fresh pick.
  allowClearExisting?: boolean;
};

export function ImageUpload({
  label,
  labelExtra,
  aspectRatio,
  onFileSelect,
  onClear,
  currentUrl,
  initialFile,
  isUploading,
  disabled,
  readOnly,
  id,
  error,
  className,
  enableCrop,
  allowClearExisting = true,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(
    initialFile ? URL.createObjectURL(initialFile) : null
  );
  const [compressing, setCompressing] = useState(false);
  const [mimeError, setMimeError] = useState<string | null>(null);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [rawFile, setRawFile] = useState<File | null>(null);

  const maxWidth = aspectRatio === "1:1" ? 800 : 1600;
  const maxHeight = aspectRatio === "1:1" ? 800 : aspectRatio === "3:1" ? 533 : 900;
  const containerClass =
    aspectRatio === "1:1"
      ? "aspect-square"
      : aspectRatio === "3:1"
        ? "aspect-[3/1]"
        : "aspect-video";
  const busy = compressing || isUploading || disabled;
  const displayUrl = preview ?? currentUrl;
  const validationError = error ?? mimeError;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setMimeError(null);
    const mime = await detectImageMime(file);
    if (!mime) {
      setMimeError("Unsupported format. Please use JPEG, PNG, WebP, or AVIF.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    if (enableCrop) {
      setCropSource(URL.createObjectURL(file));
      setRawFile(file);
      return;
    }

    compress(file, mime);
  }

  function compress(file: File, mime: string) {
    setCompressing(true);
    new Compressor(file, {
      maxWidth,
      maxHeight,
      quality: 0.85,
      success(result) {
        const compressed = new File([result], file.name, { type: result.type || mime });
        setPreview(URL.createObjectURL(compressed));
        onFileSelect(compressed);
        setCompressing(false);
      },
      error() {
        setPreview(URL.createObjectURL(file));
        onFileSelect(file);
        setCompressing(false);
      },
    });
  }

  function handleCropApply(croppedFile: File) {
    setCropSource(null);
    setRawFile(null);
    compress(croppedFile, "image/jpeg");
  }

  function handleCropCancel() {
    setCropSource(null);
    setRawFile(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClear() {
    setPreview(null);
    setMimeError(null);
    if (inputRef.current) inputRef.current.value = "";
    onClear();
  }

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center gap-1.5">
        <Label htmlFor={id}>{label}</Label>
        {labelExtra}
      </div>
      <div
        className={`relative w-full ${containerClass} ${readOnly ? "cursor-default" : busy ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        {displayUrl ? (
          <div className="relative w-full h-full">
            {readOnly ? (
              <img
                src={displayUrl}
                alt={label}
                className="w-full h-full object-cover rounded-xl border border-border"
              />
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={!!busy}
                className="block w-full h-full cursor-pointer disabled:cursor-not-allowed"
              >
                <img
                  src={displayUrl}
                  alt={label}
                  className="w-full h-full object-cover rounded-xl border border-border pointer-events-none"
                />
              </button>
            )}
            {(allowClearExisting ? displayUrl : preview) && !readOnly && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                disabled={!!busy}
                className="absolute top-2 right-2 bg-background/90 hover:bg-background rounded-full p-1 shadow border border-border disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
              >
                <X size={14} className="text-foreground" />
              </button>
            )}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/20 pointer-events-none">
                <Spinner className="size-5 border-white" />
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            id={id}
            onClick={() => inputRef.current?.click()}
            disabled={!!busy}
            className={`w-full h-full border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed ${
              validationError
                ? "border-destructive bg-destructive/5"
                : "border-border bg-muted/30 hover:border-primary hover:bg-primary/5"
            }`}
          >
            <Upload size={20} className="text-muted-foreground pointer-events-none" />
            <span className="text-xs text-muted-foreground font-medium pointer-events-none">
              {compressing ? "Compressing..." : "Upload"}
            </span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        className="hidden"
        onChange={handleFileChange}
      />
      {validationError && <p className="mt-1.5 text-sm text-destructive">{validationError}</p>}
      {enableCrop && cropSource && rawFile && (
        <ImageCropDialog
          open
          imageUrl={cropSource}
          aspect={aspectRatio === "1:1" ? 1 : aspectRatio === "3:1" ? 3 : 16 / 9}
          originalFile={rawFile}
          onApply={handleCropApply}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}
